import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DndProvider, HTML5Backend } from 'utils/dragDropContext';

import { COMPANIES } from 'constants/Entities';
import Context from 'managers/Context';
import Literal from 'components/Literal'; // FIXME Please use getLiteral here
import { TextButtonPrimary } from 'components/buttons';
import QuestionsList from './QuestionsList';
import './index.scss';

function Id(idx) {
    return `${idx}_` + Math.random().toString(36).substr(2, 9);
}

const QuestionsCrud = ({
    questions: initialQuestions,
    onChange,
    scrollBottom,
    errors,
    checkErrors,
}) => {
    const [questions, setQuestions] = useState(() => {
        let q = [...(initialQuestions || [])];
        if (q.length === 0) {
            q.push({
                _id: Id(0),
                properties: {
                    type: 'text',
                },
            });
        }
        return q;
    });
    const [accountFields, setAccountFields] = useState([]);

    useEffect(() => {
        getAccountField();
    }, []);

    const getAccountField = () => {
        let fields = Context.entityManager.getAllFields(COMPANIES).map((field) => {
            return {
                value: field.id,
                label: field.description,
                field,
            };
        });
        setAccountFields(fields);
    };

    const addQuestion = useCallback(() => {
        if (checkErrors(questions)) return;

        const newQuestions = [
            ...questions,
            {
                _id: Id(questions.length),
                properties: {
                    type: 'text',
                },
            },
        ];
        setQuestions(newQuestions);
        scrollBottom?.();
    }, [questions, checkErrors, scrollBottom]);

    const handleChange = useCallback(
        (data, i) => {
            let newQuestions = Object.assign([...questions], {
                [i]: Object.assign({}, questions[i], data),
            });

            setQuestions(newQuestions);
            onChange && onChange(newQuestions);
            checkErrors(newQuestions);
        },
        [questions, onChange, checkErrors],
    );

    const handleDelete = useCallback(
        (i) => {
            let newQuestions = [...questions.slice(0, i), ...questions.slice(i + 1)];

            setQuestions(newQuestions);
            onChange && onChange(newQuestions);
        },
        [questions, onChange],
    );

    const moveItem = useCallback(
        (fromIndex, toIndex) => {
            let newQuestions = [...questions];
            newQuestions.splice(toIndex, 0, newQuestions.splice(fromIndex, 1)[0]);
            setQuestions(newQuestions);
            onChange && onChange(newQuestions);
        },
        [questions, onChange],
    );

    const findItem = useCallback(
        (id) => {
            const question = questions.filter((c) => c && (c.questionId === id || c._id === id))[0];

            return {
                question,
                index: questions.indexOf(question),
            };
        },
        [questions],
    );

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="questions-campaigns-crud">
                <div className="questions-header">
                    <div className="questions-header-subtitle">
                        <Literal literal="label_campaigns_crud_questions_add_questions_helptext" />
                    </div>
                </div>
                <QuestionsList
                    questions={questions}
                    errors={errors}
                    handleChange={handleChange}
                    handleDelete={handleDelete}
                    moveItem={moveItem}
                    findItem={findItem}
                    accountFields={accountFields}
                />
                <div className="questions-footer">
                    <TextButtonPrimary onClick={addQuestion}>
                        <span className="questions-add-plus">+</span>
                        <Literal literal="action_campaigns_crud_questions_add_questions_add_new" />
                    </TextButtonPrimary>
                </div>
            </div>
        </DndProvider>
    );
};

QuestionsCrud.propTypes = {
    onChange: PropTypes.func,
    scrollBottom: PropTypes.func,
    questions: PropTypes.array,
    errors: PropTypes.object,
    checkErrors: PropTypes.func,
};

export default QuestionsCrud;
